<template>
  <main-input-dialog
    :is-visible.sync="computedIsVisible"
    :title-translation="!!item.id ? $i18n.t('admin.formPrint.editForm') : $i18n.t('admin.formPrint.addForm')"
    :agree-text="!!item.id ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
    dialog-width="40%"
    :loading="isLoading"
    :inputs="formPrintInputs"
    disable-default-form-bottom-padding
    @agree="onAgree"
    @modalClosed="$emit('onClose', { type: 'input', value: false })"
  >
    <template v-for="input of formPrintInputs" #[`${input.name}.input`]="data">
      <v-col v-if="Object.keys(images).includes(input.name)" :key="input.name" cols="6">
        <file-input-image
          :name="input.logoId"
          type="customLogo"
          :options="data.dropzoneOptions"
          :max-height="200"
          is-custom-deleting
          @onDelete="item[input.name] = ''"
        />
      </v-col>
      <v-col v-else-if="Object.keys(files).includes(input.name)" :key="input.name" cols="6" class="mb-4">
        <added-file-info
          v-if="!!files[input.name].name"
          :file-name="item[input.name].slice(37)"
          :is-prepend-icon="false"
          @delete="item[input.name] = ''"
        />
        <file-input v-else :options="data.dropzoneOptions" show-added-files />
      </v-col>
      <v-col v-else :key="input.name" cols="6" class="mb-4">
        <data-input v-model="item[input.name]" v-bind="data" />
      </v-col>
    </template>
  </main-input-dialog>
</template>

<script>
import { get, call } from 'vuex-pathify';
import { v4 as uuid } from 'uuid';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import DataInput from '@/components/DataInput';
import FileInput from '@/components/FileInput';
import FileInputImage from '@/components/FileInputImage';
import AddedFileInfo from '@/components/AddedFileInfo';

export default {
  name: 'FormPrintConfiguration',
  components: {
    DataInput,
    FileInput,
    FileInputImage,
    AddedFileInfo,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    images: {
      logo: {
        file: undefined,
        src: '',
        url: '',
      },
      logotype: {
        file: undefined,
        src: '',
        url: '',
      },
      logo_1: {
        file: undefined,
        src: '',
        url: '',
      },
      logo_2: {
        file: undefined,
        src: '',
        url: '',
      },
      logo_3: {
        file: undefined,
        src: '',
        url: '',
      },
      logo_4: {
        file: undefined,
        src: '',
        url: '',
      },
      legend_image: {
        file: undefined,
        src: '',
        url: '',
      },
      situation_classes_image: {
        file: undefined,
        src: '',
        url: '',
      },
    },
    files: {
      geotiff: {
        file: undefined,
        name: '',
      },
    },
  }),
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    token: get('authentication/token'),
    smartMatch: get('smartMatch/profiles'),
    smartMatchDataSourceName() {
      return this.smartMatch[this.item?.smart_match]?.datasource || '';
    },
    smartMatchArray() {
      return this.$_objectToArray(this.smartMatch).map(smartMatch => {
        return { value: smartMatch.id, text: smartMatch.name };
      });
    },
    smartMatchLayersArray() {
      return this.$_objectToArray(this.layers)
        .filter(
          layer =>
            layer.type === 'features_layer' &&
            !!layer.geometry_type &&
            layer.data_source_name === this.smartMatchDataSourceName
        )
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers)
        .filter(layer => layer.type === 'features_layer')
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },
    serviceLayersArray() {
      return this.$_objectToArray(this.layers)
        .filter(layer => layer.type === 'service_layer')
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },
    formPrintTemplates: get('formPrint/formPrintTemplates'),
    computedFormPrintTemplates() {
      return this.formPrintTemplates.map(template => {
        return { value: template.name, text: template.verbose_name };
      });
    },
    formPrintTemplatesSettings() {
      return this.formPrintTemplates.reduce((total, current) => {
        if (current.settings.length) total[current.name] = current.settings;
        return total;
      }, {});
    },
    isFormPrintSettings() {
      return !!this.formPrintTemplatesSettings[this.item?.form];
    },
    selectedLayerMetadata() {
      return this.layers?.[this.item?.layer] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.metadata?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedDataSourceAttributeId() {
      return this.metadata?.[this.selectedLayerDataSourceName]?.attributes_schema?.id_name || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
        })
      ).map(attr => {
        return { value: attr.attribute, text: attr.label };
      });
    },
    isSmartMatchLayer() {
      return this.smartMatchDataSourceName === this.selectedLayerDataSourceName;
    },
    formPrintInputs() {
      const {
        smartMatchInputs,
        serviceLayerInput,
        textInputs,
        listInput,
        imageInputs,
        geoTiffInput,
      } = this.formPrintAdditionalInputs;
      const defaultSelectParams = {
        dataType: { name: 'select' },
        align: 'start',
        rules: [v => !!v || v == '0' || 'rules.required'],
      };
      return [
        {
          name: 'name',
          translationPath: 'admin.formPrint.name',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          ...defaultSelectParams,
          name: 'form',
          translationPath: 'admin.formPrint.form',
          items: this.computedFormPrintTemplates,
        },
        {
          ...defaultSelectParams,
          name: 'smart_match',
          translationPath: 'admin.formPrint.smart_match',
          items: this.smartMatchArray,
        },
        ...smartMatchInputs,
        {
          ...defaultSelectParams,
          name: 'layer',
          translationPath: 'admin.formPrint.layer',
          subtitle: 'admin.formPrint.layerSubtitle',
          items: this.layersArray,
        },
        {
          ...defaultSelectParams,
          name: 'relation_attribute',
          translationPath: 'admin.formPrint.relation_attribute',
          subtitle: 'admin.formPrint.relationAttributeSubtitle',
          items: this.selectedLayerAttributesArray,
          disabled: !this.item?.layer || this.isSmartMatchLayer,
          rules: !this.isSmartMatchLayer ? [v => !!v || v == '0' || 'rules.required'] : [],
        },
        {
          ...defaultSelectParams,
          name: 'layer_sm',
          translationPath: 'admin.formPrint.layer',
          subtitle: 'admin.formPrint.layerSmartMatchSubtitle',
          items: this.smartMatchLayersArray,
          disabled: !this.item?.smart_match || this.isSmartMatchLayer,
          rules: !this.isSmartMatchLayer ? [v => !!v || v == '0' || 'rules.required'] : [],
        },
        ...textInputs,
        ...serviceLayerInput,
        ...listInput,
        ...imageInputs,
        ...geoTiffInput,
      ];
    },
    formPrintAdditionalInputs() {
      const smartMatchInputs =
        this.getFormPrintTemplateSettings('smart_match')?.map(input => {
          return {
            name: input.name,
            dataType: { name: 'select' },
            items: this.smartMatchArray,
            straightTitle: input.verbose_name,
            align: 'start',
            rules: [v => !!v || v == '0' || 'rules.required'],
          };
        }) || [];
      const serviceLayerInput =
        this.getFormPrintTemplateSettings('service_layer')?.map(input => {
          return {
            name: input.name,
            dataType: { name: 'select' },
            items: this.serviceLayersArray,
            straightTitle: input.verbose_name,
            align: 'start',
            rules: [v => !!v || v == '0' || 'rules.required'],
          };
        }) || [];
      const textInputs =
        this.getFormPrintTemplateSettings('text')?.map(input => {
          return {
            name: input.name,
            dataType: { name: 'textarea' },
            straightTitle: input.verbose_name,
            align: 'start',
            rows: '4',
            noResize: true,
            clearable: true,
          };
        }) || [];
      const listInput =
        this.getFormPrintTemplateSettings('list')?.map(input => {
          return {
            name: input.name,
            dataType: { name: 'select' },
            items: input.values,
            itemValue: 'name',
            itemText: 'verbose_name',
            straightTitle: input.verbose_name,
            multiple: true,
            align: 'start',
            rules: [v => !!v || v == '0' || 'rules.required'],
          };
        }) || [];
      const imageInputs =
        this.getFormPrintTemplateSettings('image')
          ?.filter(input => input.name !== 'geotiff')
          ?.map(input => {
            return {
              name: input.name,
              logoId: this.item?.[input.name],
              straightTitle: input.verbose_name,
              isCustom: true,
              align: 'start',
              dropzoneOptions: {
                url: '/',
                urlFile: this.images[input.name].src ? this.images[input.name].src : this.images[input.name].url,
                urlGet: this.images[input.name].url,
                acceptedFiles: '.png',
                maxFiles: 1,
                autoProcessQueue: false,
                init: function() {
                  this.on('addedfile', function() {
                    this.removeAllFiles(true);
                  });
                },
                accept: file => {
                  this.item[input.name] = this.item[input.name] ? this.item[input.name] : `${uuid()}.png`;
                  this.images[input.name].file = file;
                  this.images[input.name].src = URL.createObjectURL(file);
                },
              },
            };
          }) || [];
      const geoTiffInput =
        this.getFormPrintTemplateSettings('image')
          ?.filter(input => input.name === 'geotiff')
          ?.map(input => {
            return {
              name: input.name,
              straightTitle: input.verbose_name,
              isCustom: true,
              align: 'start',
              dropzoneOptions: {
                url: '/',
                acceptedFiles: '.tif,.tiff',
                maxFiles: 1,
                autoProcessQueue: false,
                init: function() {
                  this.on('addedfile', function() {
                    this.removeAllFiles(true);
                  });
                },
                accept: file => {
                  this.item[input.name] = this.files[input.name].name = this.item[input.name]
                    ? this.item[input.name]
                    : `${uuid()}_${file.name}`;
                  this.files[input.name].file = file;
                },
              },
            };
          }) || [];
      return { smartMatchInputs, serviceLayerInput, textInputs, listInput, imageInputs, geoTiffInput };
    },
  },
  watch: {
    'item.form': {
      handler(nV) {
        if (nV && this.isFormPrintSettings) {
          for (const image in this.images) {
            this.getImage(image);
          }
          for (const file in this.files) {
            this.getFileName(file);
          }
        }
      },
      immediate: true,
    },
    'item.smart_match': {
      handler(nV, oV) {
        if (nV && this.isSmartMatchLayer) {
          this.item.relation_attribute = this.selectedDataSourceAttributeId;
          this.item.layer_sm = this.item.layer;
        } else if (oV !== undefined) {
          this.item.relation_attribute = null;
          this.item.layer_sm = null;
        }
      },
      immediate: true,
    },
    'item.layer': {
      handler(nV, oV) {
        if (nV && this.isSmartMatchLayer) {
          this.item.relation_attribute = this.selectedDataSourceAttributeId;
          this.item.layer_sm = this.item.layer;
        } else if (oV !== undefined) {
          this.item.relation_attribute = null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    addImage: call('admin/putCustomLogo'),
    deleteImage: call('admin/deleteCustomLogo'),
    getImage(image) {
      if (this.item?.[image]) {
        this.images[image].url = `${process.env.VUE_APP_API_URL}/settings/logo/${this.item?.[image]}?token=${
          this.token
        }&cache=${Date.now()}`;
      }
    },
    getFileName(file) {
      if (this.item?.[file]) this.files[file].name = this.item[file];
    },
    getFormPrintTemplateSettings(type) {
      return this.formPrintTemplatesSettings[this.item?.form]?.filter(input => input.type === type);
    },
    async onAgree() {
      const settingsKeys = this.formPrintInputs
        .map(input => input.name)
        .filter(key => !['id', 'name', 'form', 'smart_match', 'layer', 'relation_attribute', 'layer_sm'].includes(key));
      try {
        this.$emit('onAgree', { type: this.item.id ? 'edit' : 'add', settingsKeys });
        for (const image in this.images) {
          if (this.isFormPrintSettings && this.images[image].file && this.images[image].src) {
            const formData = new FormData();
            formData.append('logo', this.images[image].file);
            await this.addImage({ name: this.item[image], formData });
            this.images[image].file = undefined;
            this.images[image].src = '';
          }
          if (this.item[image] === '' && !!this.images[image].url) {
            const imageName = this.images[image].url.slice(`${process.env.VUE_APP_API_URL}`.length + 15).split('?')[0];
            await this.deleteImage(imageName);
          }
        }
        for (const file in this.files) {
          if (this.isFormPrintSettings && this.files[file].file) {
            const formData = new FormData();
            formData.append('logo', this.files[file].file);
            await this.addImage({ name: this.item[file], formData });
            this.files[file].file = undefined;
            this.files[file].name = '';
          }
          if (this.item[file] === '') {
            await this.deleteImage(this.files[file].name);
          }
        }
      } catch (error) {
        console.log('  error', error);
      }
    },
  },
};
</script>
